/**
 * The finance rules shared by the phone app (apps/finance) and the desk
 * (apps/desk, docs/changes/30): labels, what a form must contain, the
 * receipt rule, the list filters, and arqam's finance errors in Arabic. Plain
 * logic only: each app keeps its own GraphQL documents (doc 17).
 */

import type { ErrorMessages } from '../errors';
import { counted, fullDate } from '../format';
import { fromCents, sumAmounts, toCents } from '../money';
import { toWesternDigits } from '../text';

export type OperationType = 'credit' | 'debit';
export type AccountType = 'cash' | 'bank_account';
export type CounterpartyKind =
  | 'supplier'
  | 'worker'
  | 'donor'
  | 'lender'
  | 'member'
  | 'institution'
  | 'other';
export type AppliesTo = 'credit' | 'debit' | 'both';
export type OperationSource =
  | 'manual'
  | 'transfer'
  | 'ledger_import'
  | 'bank_statement';

/** Where an operation comes from (arqam docs 51, 57). */
export const SOURCE_LABELS: Record<OperationSource, string> = {
  manual: 'إدخال يدوي',
  transfer: 'تحويل',
  ledger_import: 'من الدفاتر',
  bank_statement: 'من كشف بنكي',
};
export type DebtDirection = 'borrowed' | 'lent';

export const TYPE_LABELS: Record<OperationType, string> = {
  credit: 'مدخول',
  debit: 'مصروف',
};

/** Short, after an account's name in a list: «صندوق الجمعية · نقداً». */
export const ACCOUNT_TYPE_SHORT: Record<AccountType, string> = {
  cash: 'نقداً',
  bank_account: 'بنك',
};

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  cash: 'الصندوق (نقداً)',
  bank_account: 'حساب بنكي',
};

export const COUNTERPARTY_KIND_LABELS: Record<CounterpartyKind, string> = {
  supplier: 'مورد',
  worker: 'عامل',
  donor: 'متبرع',
  lender: 'مُقرض',
  member: 'منخرط',
  institution: 'مؤسسة',
  other: 'أخرى',
};

export const APPLIES_TO_LABELS: Record<AppliesTo, string> = {
  credit: 'مدخول',
  debit: 'مصروف',
  both: 'كلاهما',
};

export const DEBT_DIRECTION_LABELS: Record<DebtDirection, string> = {
  borrowed: 'اقترضته الجمعية',
  lent: 'أقرضته الجمعية',
};

/** A category for income, expenses or both fits this type (arqam doc 51). */
export const categoryFits = (appliesTo: AppliesTo, type: OperationType) =>
  appliesTo === 'both' || appliesTo === type;

/** The categories to offer for an operation of this type. */
export const categoriesFor = <T extends { appliesTo: AppliesTo }>(
  categories: T[],
  type: OperationType | null,
) => categories.filter((c) => !type || categoryFits(c.appliesTo, type));

/** The category of loan money (arqam's seeded «قروض»). */
export const LOAN_CATEGORY = 'قروض';

export const present = <T>(items: (T | null)[] | undefined): T[] =>
  (items ?? []).filter((item): item is T => item !== null);

/**
 * arqam's finance errors (its docs 33, 46, 50, 51) in Arabic. A screen adds
 * its own (e.g. a category still used).
 */
export const FINANCE_ERRORS: ErrorMessages = {
  CATEGORY_WRONG_TYPE: 'هذا الصنف لا يناسب نوع العملية',
  PROJECT_CLOSED: ({ closedOn }) =>
    typeof closedOn === 'string'
      ? `المشروع مغلق منذ ${fullDate(closedOn)}`
      : 'المشروع مغلق',
  USE_TRANSFERS: 'هذه العملية جزء من تحويل: عدّل التحويل نفسه',
  RECEIPT_PHOTO_REQUIRED: ({ minAmount }) => receiptRequiredMessage(minAmount),
  PHOTO_WRONG_KIND: 'هذه ليست صورة وصل. صوّر الوصل من جديد.',
  PHOTO_NOT_FOUND: 'تعذّر العثور على الصورة. صوّر الوصل من جديد.',
  'CHECK_VIOLATION:account_transfers_accounts_check':
    'اختر حسابين مختلفين للتحويل.',
  'CHECK_VIOLATION:financial_operations_project_check': 'اختر المشروع.',
  'CHECK_VIOLATION:counterparties_name_required': 'اكتب اسم الجهة.',
  'CHECK_VIOLATION:operation_categories_name_required': 'اكتب اسم الصنف.',
  UNIQUE_VIOLATION: 'هذا الاسم موجود من قبل.',
  INVALID_PATCH: 'تعديل غير مقبول لهذه العمليات.',
  OPERATIONS_NOT_FOUND: 'بعض العمليات لم تعد موجودة. حدّث القائمة.',
  TOO_MANY_ROWS: ({ max }) =>
    `لا يمكن تعديل أكثر من ${max ?? 500} عملية دفعة واحدة.`,
};

/** «صورة الوصل إلزامية للمصاريف ابتداءً من 500 درهم». */
export const receiptRequiredMessage = (minAmount: unknown) =>
  `صورة الوصل إلزامية للمصاريف ابتداءً من ${Number(minAmount ?? 500).toLocaleString('ar-MA')} درهم`;

export const operationsCount = (n: number) =>
  counted(n, {
    one: 'عملية واحدة',
    two: 'عمليتان',
    few: 'عمليات',
    many: 'عملية',
    hundred: 'عملية',
  });

/** "2026-10" from an operation date, to group the list by month. */
export const monthOf = (date: string) => date.slice(0, 7);

/** Operations grouped by month, keeping their order (newest first). */
export function byMonth<T extends { date: string }>(rows: T[]) {
  const groups: { month: string; rows: T[] }[] = [];
  for (const row of rows) {
    const month = monthOf(row.date);
    const last = groups[groups.length - 1];
    if (last?.month === month) last.rows.push(row);
    else groups.push({ month, rows: [row] });
  }
  return groups;
}

/** Today in Morocco as "YYYY-MM-DD" (the default operation date). */
export function today(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Casablanca',
  }).format(now);
}

/**
 * An amount as typed ("1500", "1500,50", "١٥٠٠") for the API ("1500.50"),
 * or null when it isn't a positive amount with at most two decimals.
 */
export function parseOperationAmount(input: string): string | null {
  const value = toWesternDigits(input)
    .trim()
    .replace(/[\s.](?=\d{3}(\D|$))/g, '') // thousands: "1 500" / "1.500"
    .replace(/[,٫]/g, '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const [int, dec = ''] = value.split('.');
  if (Number(int) === 0 && !/[1-9]/.test(dec)) return null;
  return `${Number(int)}.${dec.padEnd(2, '0')}`;
}

/**
 * An opening balance as typed: like an amount, but zero and negative (an
 * overdrawn bank account) are allowed. "" → "0.00"; null when unreadable.
 */
export function parseBalance(input: string): string | null {
  const text = toWesternDigits(input).trim().replace(/^[−–]/, '-');
  if (text === '' || /^-?0+([.,]0+)?$/.test(text)) return '0.00';
  const negative = text.startsWith('-');
  const amount = parseOperationAmount(negative ? text.slice(1) : text);
  if (!amount) return null;
  return negative ? `-${amount}` : amount;
}

/** An amount shown as an editable text ("1500.00" → "1500", "4.50" → "4.5"). */
export const amountText = (amount: string) => String(Number(amount));

/** A negative balance or outstanding amount (red or orange on screen). */
export const isNegative = (amount: string | null | undefined) =>
  (amount ?? '').trim().startsWith('-');

/**
 * Income, expenses and their difference, for display (arqam has no sum
 * field yet; its reports compute the official figures). Transfers and loan
 * money are left out, as in arqam's reports (doc 51).
 */
export function projectTotals(
  operations: {
    type: OperationType;
    amount: string;
    debtId?: string | null;
    transferId?: string | null;
  }[],
) {
  const kept = operations.filter((o) => !o.debtId && !o.transferId);
  const income = sumAmounts(
    kept.filter((o) => o.type === 'credit').map((o) => o.amount),
  );
  const expenses = sumAmounts(
    kept.filter((o) => o.type === 'debit').map((o) => o.amount),
  );
  return {
    income,
    expenses,
    result: fromCents(toCents(income) - toCents(expenses)),
    loanOperations: operations.filter((o) => o.debtId).length,
  };
}

export type OperationValues = {
  type: OperationType | null;
  amount: string;
  date: string;
  projectId: string | null;
  accountId: string | null;
  categoryId: string | null;
  counterpartyId: string | null;
  description: string;
  /** The receipt photo (sent in this form, or already saved). */
  receiptPhotoId: string | null;
  needsReview: boolean;
  reviewNote: string;
  /** A loan's money or repayment: kept as it is (set from the loan's page). */
  debtId: string | null;
};

/** What decides whether the receipt photo is required (arqam docs 46, 51). */
export type ReceiptRule = {
  /** settings.receiptPhotoMinAmount, e.g. "500.00"; null while loading. */
  minAmount: string | null;
  /** The saved operation when editing (null when creating). */
  saved: {
    type: OperationType;
    amount: string;
    source: OperationSource;
  } | null;
};

/**
 * arqam requires a receipt for an expense of `minAmount` or more, checked
 * when the operation is created or when its type, amount or photo changes:
 * older expenses without a photo stay editable. Imported history and
 * transfers are exempt (doc 51).
 */
export function receiptRequired(
  v: OperationValues,
  rule: ReceiptRule,
): boolean {
  const amount = parseOperationAmount(v.amount);
  if (v.type !== 'debit' || !amount || rule.minAmount === null) return false;
  if (rule.saved && rule.saved.source !== 'manual') return false;
  if (toCents(amount) < toCents(rule.minAmount)) return false;
  if (rule.saved) {
    const unchanged =
      rule.saved.type === v.type &&
      toCents(rule.saved.amount) === toCents(amount);
    if (unchanged) return false;
  }
  return true;
}

type Lists = {
  /** Categories with the type they apply to, to check the choice. */
  categories: { id: string; appliesTo: AppliesTo }[];
};

/** The operation for the API, or what is missing (in Arabic). */
export function checkOperation(
  v: OperationValues,
  rule: ReceiptRule = { minAmount: null, saved: null },
  lists: Lists = { categories: [] },
) {
  const problems: string[] = [];
  if (!v.type) problems.push('اختر: مدخول أو مصروف.');
  const amount = parseOperationAmount(v.amount);
  if (!amount) problems.push('اكتب مبلغاً صحيحاً، مثلاً 1500 أو 1500,50.');
  if (!v.date) problems.push('اختر التاريخ.');
  if (!v.accountId) problems.push('اختر الحساب (الصندوق أو البنك).');
  if (!v.projectId) problems.push('اختر المشروع.');
  const category = lists.categories.find((c) => c.id === v.categoryId);
  if (category && v.type && !categoryFits(category.appliesTo, v.type)) {
    problems.push('هذا الصنف لا يناسب نوع العملية');
  }
  if (receiptRequired(v, rule) && !v.receiptPhotoId) {
    problems.push(receiptRequiredMessage(rule.minAmount));
  }
  if (problems.length || !v.type || !amount || !v.projectId || !v.accountId) {
    return { problems, operation: null };
  }
  return {
    problems,
    operation: {
      type: v.type,
      amount,
      date: v.date,
      projectId: v.projectId,
      accountId: v.accountId,
      categoryId: v.categoryId,
      counterpartyId: v.counterpartyId,
      description: v.description.trim() || null,
      receiptPhotoId: v.receiptPhotoId,
      needsReview: v.needsReview,
      reviewNote: v.needsReview ? v.reviewNote.trim() || null : null,
      debtId: v.debtId,
    },
  };
}

export type CheckedOperation = NonNullable<
  ReturnType<typeof checkOperation>['operation']
>;

export type TransferValues = {
  date: string;
  fromAccountId: string | null;
  toAccountId: string | null;
  amount: string;
  description: string;
  receiptPhotoId: string | null;
  needsReview: boolean;
  reviewNote: string;
};

export function checkTransfer(v: TransferValues) {
  const problems: string[] = [];
  const amount = parseOperationAmount(v.amount);
  if (!v.date) problems.push('اختر التاريخ.');
  if (!v.fromAccountId) problems.push('اختر الحساب الذي خرج منه المبلغ.');
  if (!v.toAccountId) problems.push('اختر الحساب الذي دخل إليه المبلغ.');
  if (v.fromAccountId && v.fromAccountId === v.toAccountId) {
    problems.push('اختر حسابين مختلفين للتحويل.');
  }
  if (!amount) problems.push('اكتب مبلغاً صحيحاً، مثلاً 1500 أو 1500,50.');
  if (!v.description.trim()) {
    problems.push('اكتب البيان، مثلاً «إيداع مداخيل الماء في البنك».');
  }
  if (problems.length || !amount || !v.fromAccountId || !v.toAccountId) {
    return { problems, transfer: null };
  }
  return {
    problems,
    transfer: {
      date: v.date,
      fromAccountId: v.fromAccountId,
      toAccountId: v.toAccountId,
      amount,
      description: v.description.trim(),
      receiptPhotoId: v.receiptPhotoId,
      needsReview: v.needsReview,
      reviewNote: v.needsReview ? v.reviewNote.trim() || null : null,
    },
  };
}

export type CheckedTransfer = NonNullable<
  ReturnType<typeof checkTransfer>['transfer']
>;

/** The filters, kept in the address so a link can open a filtered list. */
export const FILTER_KEYS = [
  'project',
  'account',
  'type',
  'category',
  'counterparty',
  'from',
  'to',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/** arqam's filter for the chosen criteria (all run on the server). */
export function operationsFilter(
  params: URLSearchParams,
): Record<string, unknown> | null {
  const get = (key: string) => params.get(key) || null;
  const parts: Record<string, unknown>[] = [];
  const equal = (field: string, key: FilterKey) => {
    const v = get(key);
    if (v) parts.push({ [field]: { equalTo: v } });
  };
  equal('projectId', 'project');
  equal('accountId', 'account');
  equal('type', 'type');
  equal('categoryId', 'category');
  equal('counterpartyId', 'counterparty');
  const from = get('from');
  const to = get('to');
  if (from) parts.push({ date: { greaterThanOrEqualTo: from } });
  if (to) parts.push({ date: { lessThanOrEqualTo: to } });
  if (get('review')) parts.push({ needsReview: { equalTo: true } });
  // Rows with information missing (desk: filling detailed forms, doc 30).
  for (const missing of params.getAll('missing')) {
    if (missing === 'category') parts.push({ categoryExists: false });
    if (missing === 'counterparty') parts.push({ counterpartyExists: false });
    if (missing === 'receipt') {
      parts.push({ type: { equalTo: 'debit' } }, { receiptPhotoExists: false });
    }
  }
  // Transfers' operations have nothing to fill: left out of «missing» views.
  if (params.has('missing')) parts.push({ transferId: { isNull: true } });
  const q = get('q')?.trim();
  if (q) parts.push({ description: { includesInsensitive: q } });
  return parts.length ? { and: parts } : null;
}

/**
 * The operations table's filters as arqam's export filter (doc 58), and the
 * ones it can't carry (a text search, the «missing» views), to say so.
 */
export function exportFilter(params: URLSearchParams): {
  filter: Record<string, string | boolean>;
  skipped: string[];
} {
  const filter: Record<string, string | boolean> = {};
  const map: [string, string][] = [
    ['from', 'dateFrom'],
    ['to', 'dateTo'],
    ['account', 'accountId'],
    ['project', 'projectId'],
    ['category', 'categoryId'],
    ['counterparty', 'counterpartyId'],
    ['type', 'type'],
  ];
  for (const [key, field] of map) {
    const v = params.get(key);
    if (v) filter[field] = v;
  }
  if (params.get('review')) filter.needsReview = true;
  const skipped: string[] = [];
  if (params.get('q')?.trim()) skipped.push('البحث في البيان');
  if (params.getAll('missing').length)
    skipped.push('«بلا صنف / بلا جهة / بلا وصل»');
  return { filter, skipped };
}

/**
 * Pending changes grouped by identical patch, for arqam's
 * `appUpdateFinancialOperations` (one patch, many rows, all or nothing).
 */
export function groupByPatch<P extends Record<string, unknown>>(
  entries: [string, P][],
): { patch: P; ids: string[] }[] {
  const groups = new Map<string, { patch: P; ids: string[] }>();
  for (const [id, patch] of entries) {
    const key = JSON.stringify(
      Object.keys(patch)
        .sort()
        .map((k) => [k, patch[k]]),
    );
    const group = groups.get(key) ?? { patch, ids: [] };
    group.ids.push(id);
    groups.set(key, group);
  }
  return [...groups.values()];
}
